import { useMemo } from 'react'
import chunk from 'lodash/chunk'
import isNumber from 'lodash/isNumber'
import toString from 'lodash/toString'
import uniq from 'lodash/uniq'
import type { DocumentList } from '@klear/document/trader-document'
import { PAYMENT_DOCUMENT_LIST_MAX_IDS } from '@klear/payment/constants'
import { trpc } from '@klear/ui/lib/trpc'
import { filenameForContentType } from '@klear/upload/content-type'
import { buildDocumentFileUrl, withAttachmentDisposition } from '@klear/upload/file-url'

export type PaymentReceipt = { id: number; name: string; url: string }

const RECEIPT_PAGE_SIZE = 1000
const EMPTY: PaymentReceipt[] = []

function receiptsByPayment(lists: readonly (DocumentList | undefined)[], wanted: ReadonlySet<number>) {
  const byPayment = new Map<number, PaymentReceipt[]>()
  for (const { document } of lists.flatMap((list) => list?.rows ?? [])) {
    for (const reference of document.references) {
      if (reference.kind !== 'payment' || !wanted.has(reference.id)) continue
      const receipt = {
        id: document.id,
        name: filenameForContentType(document.name, document.contentType),
        url: withAttachmentDisposition(buildDocumentFileUrl(toString(document.id), document.traderId))
      }
      byPayment.set(reference.id, [...(byPayment.get(reference.id) ?? []), receipt])
    }
  }
  return byPayment
}

export function usePaymentReceipts(
  paymentIds: readonly (number | null | undefined)[],
  traderId: number
): (paymentId: number | null | undefined) => PaymentReceipt[] {
  const wanted = useMemo(
    () => uniq(paymentIds.filter((id): id is number => isNumber(id) && id > 0)).sort((a, b) => a - b),
    [paymentIds]
  )
  const combine = useMemo(() => {
    const wantedSet = new Set(wanted)
    return (results: readonly { data?: DocumentList }[]) =>
      receiptsByPayment(
        results.map((result) => result.data),
        wantedSet
      )
  }, [wanted])
  const byPayment = trpc.useQueries(
    (t) =>
      chunk(wanted, PAYMENT_DOCUMENT_LIST_MAX_IDS).map((ids) =>
        t.documents.list({ paymentIds: ids, traderId, limit: RECEIPT_PAGE_SIZE })
      ),
    { combine }
  )
  return useMemo(
    () => (paymentId: number | null | undefined) => (paymentId == null ? EMPTY : (byPayment.get(paymentId) ?? EMPTY)),
    [byPayment]
  )
}
